import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSearchParams } from 'react-router-dom';
import { billingApi } from '../api/services';
import type { BillingProduct } from '../types/models';
import BrandMark from '../components/BrandMark';

const PURPLE = '#7C3AED';
const BG_DARK = '#0A0E17';
const CARD_BG = '#121829';
const BORDER = 'rgba(255,255,255,0.08)';
const TEXT_PRIMARY = '#E8ECF4';
const TEXT_SECONDARY = '#8892A8';

function formatPrice(pence: number, currency: string): string {
  const symbol = currency.toUpperCase() === 'GBP' ? '\u00A3' : currency.toUpperCase() === 'USD' ? '$' : currency;
  return `${symbol}${Math.round(pence / 100)}`;
}

function getFeatures(plan: BillingProduct): string[] {
  const features: string[] = [];
  if (plan.aiPlanningEnabled) features.push('AI-powered daily planning');
  if (plan.transcriptProcessingEnabled) features.push('Meeting transcript analysis');
  if (plan.imageAnalysisEnabled) features.push('Image task extraction');
  if (plan.pdfExportEnabled) features.push('PDF export');
  if (plan.webhooksEnabled) features.push('Webhooks');
  if (plan.apiAccessEnabled) features.push('API access');
  if (plan.ssoEnabled) features.push('SSO / SAML');
  if (plan.customBrandingEnabled) features.push('Custom branding');
  if (plan.crossExecutiveReportingEnabled) features.push('Cross-executive reporting');
  if (plan.prioritySupportEnabled) features.push('Priority support');
  if (plan.dedicatedOnboardingEnabled) features.push('Dedicated onboarding');
  if (plan.maxExecutiveSeats > 0) {
    features.push(`Up to ${plan.maxExecutiveSeats} executive seat${plan.maxExecutiveSeats > 1 ? 's' : ''}`);
  }
  return features;
}

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan')?.toLowerCase() || '';

  const [plans, setPlans] = useState<BillingProduct[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [orgName, setOrgName] = useState('');
  const [executiveName, setExecutiveName] = useState('');
  const [email, setEmail] = useState('');
  const [seatCount, setSeatCount] = useState(1);

  useEffect(() => {
    billingApi.getPlans()
      .then((data) => {
        setPlans(data);
        // Pre-select based on query param
        const match = data.find(p => p.name.toLowerCase().includes(preselectedPlan));
        if (match) {
          setSelectedPlanId(match.id);
        } else {
          // Default to popular plan
          const popular = data.find(p => p.isPopular);
          if (popular) setSelectedPlanId(popular.id);
        }
      })
      .catch(() => {
        setError('Unable to load plans. Please refresh the page.');
      })
      .finally(() => setLoading(false));
  }, [preselectedPlan]);

  const selectedPlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlanId) {
      setError('Please select a plan.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { checkoutUrl } = await billingApi.checkout({
        productId: selectedPlanId,
        email,
        organisationName: orgName,
        executiveName,
        seatCount,
      });
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.title || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ bgcolor: BG_DARK }}
      >
        <CircularProgress sx={{ color: PURPLE, mb: 3 }} size={48} />
        <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
          Redirecting to secure payment...
        </Typography>
        <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mt: 1 }}>
          You will be redirected to Stripe to complete your subscription.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      sx={{
        bgcolor: BG_DARK,
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Box maxWidth={900} mx="auto">
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Box display="inline-flex" sx={{ mb: 0.5 }}>
            <BrandMark size="md" showStratiumLine accent="light" />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: TEXT_SECONDARY, mt: 1.5, fontSize: '0.95rem' }}
          >
            Executive orchestration platform
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: TEXT_PRIMARY,
              fontWeight: 600,
              mt: 4,
              letterSpacing: '-0.01em',
            }}
          >
            Choose your plan
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: TEXT_SECONDARY, mt: 1 }}
          >
            Start with a 14-day free trial. No charge until your trial ends.
          </Typography>
        </Box>

        {/* Plan Cards */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <Grid container spacing={3} mb={6}>
            {plans.map((plan) => {
              const isSelected = plan.id === selectedPlanId;
              const isEnterprise = plan.isCustomPricing;
              const features = getFeatures(plan);

              return (
                <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
                  <Card
                    onClick={() => !isEnterprise && setSelectedPlanId(plan.id)}
                    sx={{
                      cursor: isEnterprise ? 'default' : 'pointer',
                      height: '100%',
                      bgcolor: CARD_BG,
                      border: `2px solid ${isSelected ? PURPLE : BORDER}`,
                      boxShadow: isSelected ? `0 0 24px ${PURPLE}33` : 'none',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      '&:hover': !isEnterprise ? {
                        borderColor: isSelected ? PURPLE : 'rgba(255,255,255,0.15)',
                      } : {},
                    }}
                  >
                    {plan.isPopular && (
                      <Chip
                        label="Most popular"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: PURPLE,
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ color: TEXT_PRIMARY, fontWeight: 600, mb: 1 }}
                      >
                        {plan.name}
                      </Typography>

                      {isEnterprise ? (
                        <Box mb={2}>
                          <Typography variant="h5" sx={{ color: TEXT_PRIMARY, fontWeight: 700 }}>
                            Custom
                          </Typography>
                          <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem' }}>
                            Tailored to your organisation
                          </Typography>
                        </Box>
                      ) : (
                        <Box mb={2}>
                          <Typography
                            component="span"
                            sx={{ color: TEXT_PRIMARY, fontWeight: 700, fontSize: '2rem', lineHeight: 1 }}
                          >
                            {formatPrice(plan.pricePerSeatPence, plan.currency)}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{ color: TEXT_SECONDARY, fontSize: '0.85rem', ml: 0.5 }}
                          >
                            /exec/mo
                          </Typography>
                        </Box>
                      )}

                      {plan.description && (
                        <Typography
                          variant="body2"
                          sx={{ color: TEXT_SECONDARY, mb: 2, fontSize: '0.85rem' }}
                        >
                          {plan.description}
                        </Typography>
                      )}

                      <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
                        {features.map((feature) => (
                          <Box
                            component="li"
                            key={feature}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{ color: PURPLE, fontSize: 18, mt: '2px', flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: TEXT_SECONDARY, fontSize: '0.82rem' }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {isEnterprise && (
                        <Button
                          variant="outlined"
                          fullWidth
                          href="https://www.stratiumhq.com/contact"
                          sx={{
                            mt: 2,
                            borderColor: PURPLE,
                            color: PURPLE,
                            fontWeight: 600,
                            '&:hover': {
                              borderColor: PURPLE,
                              bgcolor: `${PURPLE}15`,
                            },
                          }}
                        >
                          Contact sales
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Registration Form */}
        {!loading && (
          <Box maxWidth={480} mx="auto">
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Card
              sx={{
                bgcolor: CARD_BG,
                border: `1px solid ${BORDER}`,
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{ color: TEXT_PRIMARY, fontWeight: 600, mb: 3, textAlign: 'center' }}
                >
                  Create your account
                </Typography>

                <form onSubmit={handleSubmit}>
                  <TextField
                    label="Organisation Name"
                    fullWidth
                    required
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    sx={{ mb: 2.5 }}
                    InputProps={{
                      sx: { bgcolor: BG_DARK },
                    }}
                  />
                  <TextField
                    label="Your Name"
                    fullWidth
                    required
                    value={executiveName}
                    onChange={(e) => setExecutiveName(e.target.value)}
                    sx={{ mb: 2.5 }}
                    InputProps={{
                      sx: { bgcolor: BG_DARK },
                    }}
                  />
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 2.5 }}
                    InputProps={{
                      sx: { bgcolor: BG_DARK },
                    }}
                  />
                  <TextField
                    label="Number of Executive Seats"
                    type="number"
                    fullWidth
                    required
                    value={seatCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (val >= 1) {
                        const max = selectedPlan?.maxExecutiveSeats || 999;
                        setSeatCount(Math.min(val, max));
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: selectedPlan?.maxExecutiveSeats || 999,
                    }}
                    InputProps={{
                      sx: { bgcolor: BG_DARK },
                    }}
                    sx={{ mb: 3 }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!selectedPlanId || selectedPlan?.isCustomPricing}
                    sx={{
                      bgcolor: PURPLE,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '1rem',
                      py: 1.5,
                      '&:hover': {
                        bgcolor: '#6D28D9',
                      },
                      '&.Mui-disabled': {
                        bgcolor: `${PURPLE}55`,
                        color: 'rgba(255,255,255,0.4)',
                      },
                    }}
                  >
                    Start 14-day free trial
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Footer */}
            <Typography
              variant="body2"
              sx={{
                color: TEXT_SECONDARY,
                textAlign: 'center',
                mt: 4,
                mb: 2,
                fontSize: '0.82rem',
              }}
            >
              14-day free trial. No charge until trial ends. Cancel anytime.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
